import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import { useAuth } from '@/context/AuthContext';

interface TelegramSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export default function TelegramSettingsModal({
  open,
  onClose,
}: TelegramSettingsModalProps) {
  const { user } = useAuth();
  const [token, setToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState<'idle' | 'ok' | 'fail'>('idle');

  const load = async () => {
    setLoading(true);
    setError('');
    const { data, error: err } = await supabase
      .from('noteapp_settings')
      .select('telegram_bot_token, telegram_chat_id, telegram_enabled')
      .eq('user_id', user?.id)
      .maybeSingle();

    if (err) {
      setError('Không thể tải cài đặt hiện tại.');
    } else if (data) {
      setToken(data.telegram_bot_token ?? '');
      setChatId(data.telegram_chat_id ?? '');
      setEnabled(data.telegram_enabled ?? false);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      setSuccess('');
      setTestResult('idle');
      load();
    }
  }, [open]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user) {
      setError('Bạn cần đăng nhập để lưu cài đặt.');
      return;
    }

    if (enabled && (!token.trim() || !chatId.trim())) {
      setError('Vui lòng nhập Bot Token và Chat ID trước khi bật cảnh báo.');
      return;
    }

    setSaving(true);
    const payload = {
      telegram_bot_token: token.trim() || null,
      telegram_chat_id: chatId.trim() || null,
      telegram_enabled: enabled,
    };

    const { data: existing } = await supabase
      .from('noteapp_settings')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    const result = existing
      ? await supabase.from('noteapp_settings').update(payload).eq('id', existing.id)
      : await supabase.from('noteapp_settings').insert({ ...payload, user_id: user.id });

    setSaving(false);

    if (result.error) {
      setError('Không thể lưu cài đặt. Vui lòng thử lại.');
      return;
    }

    setSuccess('Đã lưu cài đặt cảnh báo Telegram.');
  };

  const handleTest = async () => {
    setError('');
    setSuccess('');
    setTestResult('idle');

    if (!token.trim() || !chatId.trim()) {
      setError('Nhập Bot Token và Chat ID trước khi gửi tin thử.');
      return;
    }

    setTesting(true);
    const { data, error: err } = await supabase.functions.invoke(
      'noteapp-notify-expiring-notes',
      {
        body: { action: 'test', token: token.trim(), chat: chatId.trim() },
      }
    );
    setTesting(false);

    if (err) {
      setTestResult('fail');
      return;
    }
    setTestResult(data?.ok ? 'ok' : 'fail');
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cảnh báo Telegram"
      maxWidth="max-w-md"
    >
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          <div className="flex items-start gap-2.5 rounded-lg bg-primary-100/70 px-4 py-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center">
              <i className="ri-send-plane-2-line text-lg text-primary-600" />
            </span>
            <div className="text-sm text-foreground-700">
              <p className="font-medium text-foreground-900">
                Nhận cảnh báo khi ghi chú sắp hết hạn
              </p>
              <p className="mt-0.5">
                Hệ thống tự kiểm tra và gửi tin mỗi ngày lúc{' '}
                <span className="font-semibold text-foreground-900">
                  7:00 sáng (GMT+7)
                </span>
                .
              </p>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">
              Bot Token
            </label>
            <div className="relative">
              <input
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="123456789:ABCdefGHI..."
                autoComplete="off"
                className={`${inputClass} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowToken((v) => !v)}
                aria-label={showToken ? 'Ẩn token' : 'Hiện token'}
                className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-100 hover:text-foreground-700"
              >
                <i
                  className={
                    showToken ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'
                  }
                />
              </button>
            </div>
            <p className="mt-1.5 text-xs text-foreground-400">
              Lấy token từ @BotFather khi tạo bot trên Telegram.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-700">
              Chat ID
            </label>
            <input
              type="text"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="123456789"
              autoComplete="off"
              className={inputClass}
            />
            <p className="mt-1.5 text-xs text-foreground-400">
              Lấy Chat ID qua @userinfobot.
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-foreground-800">
                Bật cảnh báo tự động
              </p>
              <p className="text-xs text-foreground-400">
                Tắt để tạm dừng nhận cảnh báo.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => setEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                enabled ? 'bg-primary-500' : 'bg-background-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-background-50 transition ${
                  enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
              <i className="ri-error-warning-line mt-0.5 text-base" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-800">
              <i className="ri-checkbox-circle-line mt-0.5 text-base" />
              <span>{success}</span>
            </div>
          )}
          {testResult === 'ok' && (
            <div className="flex items-start gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-800">
              <i className="ri-checkbox-circle-line mt-0.5 text-base" />
              <span>Đã gửi tin thử thành công. Kiểm tra Telegram nhé.</span>
            </div>
          )}
          {testResult === 'fail' && (
            <div className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
              <i className="ri-error-warning-line mt-0.5 text-base" />
              <span>
                Gửi tin thử thất bại. Kiểm tra lại Bot Token và Chat ID.
              </span>
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={handleTest}
              disabled={testing}
              className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {testing ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-foreground-500 border-t-transparent" />
              ) : (
                <i className="ri-send-plane-line text-base" />
              )}
              Gửi tin thử
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50 border-t-transparent" />
              )}
              Lưu cài đặt
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}